import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import AdminPage from '../../components/admin/AdminPage';
import MetricCard from '../../components/admin/MetricCard';
import SectionCard from '../../components/admin/SectionCard';
import StatusChip from '../../components/admin/StatusChip';
import { Cause, DonationTransactionRow } from '../../types';
import { ReportingService } from '../../services/ReportingService';
import { CauseService } from '../../services/CauseService';

const ReportingPage: React.FC = () => {
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    causeId: '',
    status: '',
  });
  const [reportData, setReportData] = useState<DonationTransactionRow[]>([]);
  const [causes, setCauses] = useState<Cause[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isReportGenerated, setIsReportGenerated] = useState(false);

  useEffect(() => {
    const fetchCauses = async () => {
      try {
        const causesData = await CauseService.getCauses();
        setCauses(causesData);
      } catch (err) {
        console.error('Failed to fetch causes for filter', err);
        setError('Could not load causes for filtering.');
      }
    };
    fetchCauses();
  }, []);

  const handleGenerateReport = async () => {
    setLoading(true);
    setError(null);
    setIsReportGenerated(true);
    try {
      const data = await ReportingService.listDonationTransactions(filters);
      setReportData(data);
    } catch (err) {
      console.error('Failed to generate report', err);
      setError('Failed to load transaction log. Please try again.');
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = () => {
    if (reportData.length === 0) return;

    const headers = [
      'Donation ID',
      'Status',
      'Raw Status',
      'Amount',
      'Cause Title',
      'User Email',
      'Merchant Reference',
      'APS Transaction Ref',
      'Created At',
      'Resolved At',
      'Failure Message',
    ];
    const csvRows = [
      headers.join(','),
      ...reportData.map((row) =>
        [
          `"${row.donationId}"`,
          `"${row.statusCategory}"`,
          `"${row.status}"`,
          row.amount,
          `"${(row.causeTitle || '').replace(/"/g, '""')}"`,
          `"${row.userEmail}"`,
          `"${row.merchantReference}"`,
          `"${row.providerTransactionRef || ''}"`,
          `"${new Date(row.createdAt).toISOString()}"`,
          `"${new Date(
            row.succeededAt || row.failedAt || row.cancelledAt || row.updatedAt
          ).toISOString()}"`,
          `"${(row.failureMessage || '').replace(/"/g, '""')}"`,
        ].join(',')
      ),
    ];

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'payment_transactions.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const summary = useMemo(() => {
    const totalAmount = reportData.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const successCount = reportData.filter(
      (row) => row.statusCategory === 'success'
    ).length;
    const failedCount = reportData.filter(
      (row) => row.statusCategory === 'failed'
    ).length;
    const pendingCount = reportData.filter(
      (row) => row.statusCategory === 'pending'
    ).length;

    return {
      totalTransactions: reportData.length,
      totalAmount,
      successCount,
      failedCount,
      pendingCount,
    };
  }, [reportData]);

  const formatResolvedAt = (row: DonationTransactionRow) =>
    row.succeededAt || row.failedAt || row.cancelledAt || row.updatedAt;

  return (
    <AdminPage
      title="Payment Transactions"
      description="Review APS-backed donation transactions and export their current payment status."
      action={
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="contained"
            onClick={handleGenerateReport}
            disabled={loading}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Load transactions'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExportCsv}
            disabled={loading || reportData.length === 0}
          >
            Export CSV
          </Button>
        </Stack>
      }
    >
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <MetricCard
            label="Rows returned"
            value={String(summary.totalTransactions)}
            helperText="Transactions matching current filters"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            label="Total amount"
            value={`EGP ${summary.totalAmount.toFixed(2)}`}
            helperText="Sum of filtered transactions"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            label="Successful"
            value={String(summary.successCount)}
            helperText="Transactions marked successful"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MetricCard
            label="Pending"
            value={String(summary.pendingCount)}
            helperText="Transactions still awaiting final outcome"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MetricCard
            label="Failed"
            value={String(summary.failedCount)}
            helperText="Failed or cancelled transactions"
          />
        </Grid>
      </Grid>

      <SectionCard title="Filters" description="Narrow the transaction log before loading results.">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              name="startDate"
              label="Start date"
              type="date"
              fullWidth
              value={filters.startDate}
              onChange={(event) =>
                setFilters({ ...filters, startDate: event.target.value })
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              name="endDate"
              label="End date"
              type="date"
              fullWidth
              value={filters.endDate}
              onChange={(event) =>
                setFilters({ ...filters, endDate: event.target.value })
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel id="status-select-label">Status</InputLabel>
              <Select
                labelId="status-select-label"
                name="status"
                value={filters.status}
                label="Status"
                onChange={(event) =>
                  setFilters({ ...filters, status: event.target.value })
                }
              >
                <MenuItem value="">
                  <em>All statuses</em>
                </MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel id="cause-select-label">Cause</InputLabel>
              <Select
                labelId="cause-select-label"
                name="causeId"
                value={filters.causeId}
                label="Cause"
                onChange={(event) =>
                  setFilters({ ...filters, causeId: event.target.value })
                }
              >
                <MenuItem value="">
                  <em>All causes</em>
                </MenuItem>
                {causes.map((cause) => (
                  <MenuItem key={cause.id} value={cause.id}>
                    {cause.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </SectionCard>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {!loading && isReportGenerated && (
        <SectionCard
          title="Transaction Log"
          description="Current APS transaction outcomes captured by the app backend."
        >
          {reportData.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Donation ID</TableCell>
                    <TableCell>Merchant Ref</TableCell>
                    <TableCell>APS Ref</TableCell>
                    <TableCell>Cause</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Resolved</TableCell>
                    <TableCell>Failure</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <StatusChip value={row.statusCategory} />
                          <Typography variant="caption" color="text.secondary">
                            {row.status}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{row.donationId}</TableCell>
                      <TableCell>{row.merchantReference}</TableCell>
                      <TableCell>{row.providerTransactionRef || '-'}</TableCell>
                      <TableCell>{row.causeTitle || row.causeId || '-'}</TableCell>
                      <TableCell>{row.userEmail}</TableCell>
                      <TableCell>{new Date(row.createdAt).toLocaleString()}</TableCell>
                      <TableCell>{new Date(formatResolvedAt(row)).toLocaleString()}</TableCell>
                      <TableCell>{row.failureMessage || '-'}</TableCell>
                      <TableCell>{row.provider}</TableCell>
                      <TableCell align="right">
                        {row.currency} {Number(row.amount).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box>
              <Typography>No transactions found for the selected filters.</Typography>
            </Box>
          )}
        </SectionCard>
      )}
    </AdminPage>
  );
};

export default ReportingPage;
